import type { ReactNode } from 'react';

/**
 * Exercise pictograms — one per exercise, keyed by exercise id.
 *
 * Drawn the way the app mark is drawn: a circle head and single-stroke limbs
 * with round caps, in ink, on a 48-unit grid. Each one shows the actual
 * position (side view unless the movement only reads from the front, or from
 * above for side-lying work), with a light floor or prop where the body rests
 * on one and an arrow where the movement has a direction. Original artwork;
 * nothing traced.
 *
 * Every active exercise has its own entry; archived ids keep theirs so
 * history still draws.
 *
 * `fallback` is the coarse `illustration` field on the exercise, for any
 * future exercise without its own drawing yet.
 */
const floor = <path d="M4 42.5h40" className="prop" />;

/** Arrowhead at (x, y) pointing in `dir`. */
function head(x: number, y: number, dir: 'up' | 'down' | 'left' | 'right'): string {
  const d = 2.6;
  switch (dir) {
    case 'up':
      return `M${x - d} ${y + d}L${x} ${y}L${x + d} ${y + d}`;
    case 'down':
      return `M${x - d} ${y - d}L${x} ${y}L${x + d} ${y - d}`;
    case 'left':
      return `M${x + d} ${y - d}L${x} ${y}L${x + d} ${y + d}`;
    case 'right':
      return `M${x - d} ${y - d}L${x} ${y}L${x - d} ${y + d}`;
  }
}

const chinTuck = (
  <>
    <circle cx="30" cy="15" r="5" className="ghost" />
    <circle cx="22" cy="15" r="5" />
    <path d="M21 21v21M21 26l7 9" />
    <path d={`M35 6h-8${head(27, 6, 'left')}`} className="cue" />
  </>
);

/** Seated, upright on the sitting bones — the base for the posture drills. */
const seatedChair = <path d="M13 31h16M14 31v12M28 31v12" className="prop" />;

const DRAWINGS: Record<string, ReactNode> = {
  // ── cervical ──────────────────────────────────────────────────────────────
  'chin-tuck': chinTuck,
  'cervical-retraction-hold': chinTuck,

  'chin-tuck-supine': (
    <>
      {floor}
      <circle cx="9" cy="37.5" r="4.5" />
      <path d="M13.5 39.5H30M30 39.5l6-10 5 13" />
      <path d={`M19 29H8${head(8, 29, 'left')}`} className="cue" />
    </>
  ),

  'chin-tuck-towel': (
    <>
      <circle cx="30" cy="15" r="5" className="ghost" />
      <circle cx="22" cy="15" r="5" />
      <path d="M21 21v21" />
      <path d="M21 26l5 6 6-5" />
      <path d="M32 27L17.5 21q-1.5-1 0-2.5" className="prop" />
      <path d={`M35 6h-8${head(27, 6, 'left')}`} className="cue" />
    </>
  ),

  // Front view: one hand holds the opposite collarbone down, head tips away.
  'scalene-stretch': (
    <>
      <circle cx="29" cy="12" r="4.5" />
      <path d="M24 22l2.5-5.5" />
      <path d="M24 22v21M17 23h14" />
      <path d="M17 23l-2 13" />
      <path d="M31 23v9l-10-6" />
      <path d={`M36 15a5 5 0 0 0-1-7${head(35, 8, 'up')}`} className="cue" />
    </>
  ),

  // Both hands on one collarbone, head tipped away and turning back (arrow).
  'scm-stretch': (
    <>
      <circle cx="29" cy="12" r="4.5" />
      <path d="M24 22l2.5-5.5" />
      <path d="M24 22v21M17 23h14" />
      <path d="M17 23l-4 8 7-5" />
      <path d="M31 23v9l-10-6" />
      <path d={`M23 4.5a6 2.5 0 0 1 12 0${head(35, 4.5, 'down')}`} className="cue" />
    </>
  ),

  'wall-angel': (
    <>
      <circle cx="24" cy="9.5" r="4.5" />
      <path d="M24 15v16M24 31l-5 12M24 31l5 12" />
      <path d="M24 18l-10 1-1-11M24 18l10 1 1-11" />
    </>
  ),

  'serratus-wall-slide': (
    <>
      <path d="M39 4v40" className="prop" />
      <circle cx="26" cy="10" r="4.5" />
      <path d="M25 15.5l-1 17M24 32.5l-1 11" />
      <path d="M25 19l11 5v-14" />
      <path d={`M44 20V9${head(44, 9, 'up')}`} className="cue" />
    </>
  ),

  'doorway-pec-stretch': (
    <>
      <path d="M17 4v40" className="prop" />
      <circle cx="31" cy="10" r="4.5" />
      <path d="M30 15.5l-2 15M28 30.5l5 13M28 30.5l-5 13" />
      <path d="M29.5 19H18v-9" />
    </>
  ),

  'upper-trap-stretch': (
    <>
      <circle cx="28" cy="13" r="4.5" />
      <path d="M24 21l1.5-3" />
      <path d="M24 21v22" />
      <path d="M24 23l14-6-4-9" />
      <path d="M24 23l-8 12" />
    </>
  ),

  'levator-stretch': (
    <>
      <circle cx="29" cy="17" r="4.5" />
      <path d="M24 22l1.5-1" />
      <path d="M24 22v21" />
      <path d="M24 24l15-5-3-9" />
      <path d="M24 24l-8 12" />
    </>
  ),

  'suboccipital-release': (
    <>
      {floor}
      <circle cx="9" cy="35" r="4.5" />
      <circle cx="9" cy="41" r="1.5" className="prop-fill" />
      <path d="M14 37.5h17M31 37.5l5-9 5 13.5" />
    </>
  ),

  'deep-neck-flexor': (
    <>
      {floor}
      <circle cx="10" cy="33" r="4.5" />
      <path d="M14.5 38H31M31 38l5-9 5 13" />
      <path d={`M6 25a7 7 0 0 1 8-2${head(14, 23, 'right')}`} className="cue" />
    </>
  ),

  'foam-roller-thoracic': (
    <>
      {floor}
      <circle cx="21" cy="38" r="4" className="prop" />
      <circle cx="9" cy="37" r="4.5" />
      <path d="M13 33.5Q21 26 33 40M33 40l5-9 5 11" />
    </>
  ),

  'cat-cow': (
    <>
      {floor}
      <circle cx="8" cy="30" r="4.5" />
      <path d="M12 26Q23 12 34 26" />
      <path d="M12 26v16M34 26v16h8" />
      <path d={`M23 2.5v10${head(23, 2.5, 'up')}${head(23, 12.5, 'down')}`} className="cue" />
    </>
  ),

  'prone-ytw': (
    <>
      {floor}
      <circle cx="11" cy="37.5" r="4.5" />
      <path d="M16 39h28" />
      <path d="M18 37L5 25" />
      <path d={`M27 33v-8${head(27, 25, 'up')}`} className="cue" />
    </>
  ),

  'seated-thoracic-rotation': (
    <>
      <path d="M15 31h15M16 31v12M29 31v12" className="prop" />
      <circle cx="21" cy="9" r="4.5" />
      <path d="M21 14v16M21 30h12l1 13" />
      <path d="M21 19l6 3" />
      <path d={`M12 20a10 4 0 0 0 19 1.5${head(31, 21.5, 'up')}`} className="cue" />
    </>
  ),

  // Seen from above: lying on the side, knees bent forward, top arm opening back.
  'open-book': (
    <>
      <circle cx="14.5" cy="24" r="4.5" />
      <path d="M19 24H35M35 24v11h9" />
      <path d="M22 24v12M22 24V12" />
      <path d={`M22 39a15 15 0 0 1 0-30${head(22, 9, 'right')}`} className="cue" />
    </>
  ),

  'seated-thoracic-extension': (
    <>
      <path d="M15 31h15M16 31v12M29 31v12M15 31V19" className="prop" />
      <circle cx="11" cy="10" r="4.5" />
      <path d="M19 30Q17.5 21 14 15.5" />
      <path d="M19 30h11l1 13" />
      <path d="M15.5 18l7 5" />
      <path d={`M29 14a9 9 0 0 0-8-9${head(21, 5, 'left')}`} className="cue" />
    </>
  ),

  'lat-stretch': (
    <>
      {floor}
      <path d="M33 21h11M42 21v21.5" className="prop" />
      <circle cx="24.5" cy="28" r="4.5" />
      <path d="M19 23l14-2M19 23l2 2" />
      <path d="M19 23L8 21l3 10.5-2 11" />
      <path d={`M11 13H4${head(4, 13, 'left')}`} className="cue" />
    </>
  ),

  'standing-w-retraction': (
    <>
      <circle cx="24" cy="9" r="4.5" />
      <path d="M24 14.5V30M24 30l-5 12.5M24 30l5 12.5" />
      <path d="M24 18l-8 7-2-11M24 18l8 7 2-11" />
      <path d={`M4 31h5${head(9, 31, 'right')}`} className="cue" />
      <path d={`M44 31h-5${head(39, 31, 'left')}`} className="cue" />
    </>
  ),

  'seated-lat-activation': (
    <>
      {seatedChair}
      <circle cx="21" cy="9" r="4.5" />
      <path d="M21 14.5V30M21 30h11l1 13" />
      <path d="M21 18l-3 12.5" />
      <path d={`M12 11v7${head(12, 18, 'down')}`} className="cue" />
    </>
  ),

  'standing-y': (
    <>
      <circle cx="24" cy="13" r="4.5" />
      <path d="M24 18.5V31M24 31l-5 12M24 31l5 12" />
      <path d="M24 20L11 7M24 20L37 7" />
      <path d={`M6 27v-8${head(6, 19, 'up')}`} className="cue" />
      <path d={`M42 27v-8${head(42, 19, 'up')}`} className="cue" />
    </>
  ),

  // Face down, forehead on a towel, arms lifting through the three letters.
  'prone-ity': (
    <>
      {floor}
      <rect x="4" y="39" width="13" height="3.5" rx="1.5" className="prop" />
      <circle cx="10.5" cy="34" r="4.5" />
      <path d="M15 37l4 2.5H44" />
      <path d="M19 39.5L31 36" />
      <path d="M12 6v8M18 6h8M22 6v8M29 6l4 4.5 4-4.5M33 10.5V14" className="cue" />
    </>
  ),

  // ── posture ───────────────────────────────────────────────────────────────
  'upright-hold': (
    <>
      {seatedChair}
      <circle cx="21" cy="9" r="4.5" />
      <path d="M21 14.5V30M21 30h11l1 13" />
      <path d="M21 18l2 9 7 1" />
      <path d={`M35 9h-7${head(28, 9, 'left')}`} className="cue" />
      <path d={`M17 18h-7${head(10, 18, 'left')}`} className="cue" />
    </>
  ),

  'string-overhead': (
    <>
      <path d="M13 33h16M14 33v10M28 33v10" className="prop" />
      <circle cx="21" cy="12.5" r="4.5" />
      <path d="M21 18V32M21 32h11l1 11" />
      <path d="M21 21l2 9 7 1" />
      <path d="M20 7.5V1" className="cue" strokeDasharray="1.5 3" />
      <path d={`M31 16V7${head(31, 7, 'up')}`} className="cue" />
    </>
  ),

  'bird-dog-chin-tuck': (
    <>
      {floor}
      <circle cx="7" cy="15.5" r="4.5" className="ghost" />
      <circle cx="10" cy="21" r="4.5" />
      <path d="M14 26h18" />
      <path d="M15 26v16M14 26l-11 1" />
      <path d="M32 26v16h7M32 26l13-2" />
    </>
  ),

  'thread-the-needle': (
    <>
      {floor}
      <circle cx="10" cy="37.5" r="4.5" />
      <path d="M15 33L32 26M32 26v16h9" />
      <path d="M18 32v10" />
      <path d="M15 35.5l16 5" />
    </>
  ),

  'scapular-retraction': (
    <>
      <circle cx="24" cy="9" r="4.5" />
      <path d="M24 14.5V43" />
      <path d="M24 18l-8 2-1 13M24 18l8 2 1 13" />
      <path d={`M5 25h6${head(11, 25, 'right')}`} className="cue" />
      <path d={`M43 25h-6${head(37, 25, 'left')}`} className="cue" />
    </>
  ),

  'band-pull-apart': (
    <>
      <circle cx="24" cy="9" r="4.5" />
      <path d="M24 14.5V43" />
      <path d="M24 20H9M24 20h15" />
      <path d="M9 24h30" className="prop" />
      <path d={`M7 28H3${head(3, 28, 'left')}`} className="cue" />
      <path d={`M41 28h4${head(45, 28, 'right')}`} className="cue" />
    </>
  ),

  'prone-row': (
    <>
      <circle cx="8" cy="17" r="4.5" />
      <path d="M12.5 20L31 25M31 25l-2 18M31 25l4 18" />
      <path d="M15 21.5v12" />
      <path d="M12 35h6" className="prop" />
      <path d={`M22 36v-9${head(22, 27, 'up')}`} className="cue" />
    </>
  ),

  'dead-bug': (
    <>
      {floor}
      <circle cx="8" cy="37.5" r="4.5" />
      <path d="M13 39.5h17" />
      <path d="M16 39V24M16 39l5-14" />
      <path d="M30 39.5l2-10h9M30 39.5l14-4" />
    </>
  ),

  'bird-dog': (
    <>
      {floor}
      <circle cx="10" cy="20" r="4.5" />
      <path d="M14 26h18" />
      <path d="M15 26v16M14 26l-11 1" />
      <path d="M32 26v16h7M32 26l13-2" />
    </>
  ),

  'glute-bridge': (
    <>
      {floor}
      <circle cx="7" cy="38" r="4.5" />
      <path d="M12 40l15-12 8-3 2 17" />
      <path d="M12 41h10" />
      <path d={`M26 20v-7${head(26, 13, 'up')}`} className="cue" />
    </>
  ),

  'pelvic-tilt': (
    <>
      {floor}
      <circle cx="8" cy="37" r="4.5" />
      <path d="M13 39.5h15l6-11 5 14" />
      <path d={`M21 32a5 5 0 1 1 7 0${head(28, 32, 'down')}`} className="cue" />
    </>
  ),

  // Standing split stance: back heel up, pelvis tucked, weight easing forward.
  'hip-flexor-stretch': (
    <>
      {floor}
      <circle cx="22" cy="8" r="4.5" />
      <path d="M22 13.5V28" />
      <path d="M22 28l8 6v8.5h4" />
      <path d="M22 28L11 40l-3 2.5" />
      <path d="M22 17l-2 10" />
      <path d={`M30 24h6${head(36, 24, 'right')}`} className="cue" />
    </>
  ),

  'hamstring-stretch': (
    <>
      {floor}
      <path d="M8 31h13M9 31v11.5M20 31v11.5" className="prop" />
      <circle cx="27" cy="12.5" r="4.5" />
      <path d="M15 30L23.5 17" />
      <path d="M15 30.5L38 42l2-4" />
      <path d="M15 30h9v12.5" />
      <path d="M22 19.5l3 10" />
      <path d={`M30 5h7${head(37, 5, 'right')}`} className="cue" />
    </>
  ),

  'ninety-ninety-breathing': (
    <>
      {floor}
      <path d="M30 29h14v13.5" className="prop" />
      <circle cx="7" cy="38" r="4.5" />
      <path d="M12 40h12V27h16" />
      <path d="M13 32q2-3 4 0t4 0" className="cue" />
    </>
  ),

  'side-plank-knees': (
    <>
      {floor}
      <circle cx="10" cy="22" r="4.5" />
      <path d="M13 27l20 12l8-6" />
      <path d="M13.5 28v14.5h6" />
      <path d="M15 28l3-12" />
    </>
  ),

  'standing-hip-hinge': (
    <>
      <circle cx="9" cy="18" r="4.5" />
      <path d="M14 20l16 7M30 27l-1 16M30 27l4 16" />
      <path d="M16 21l1 11" />
      <path d="M6 13l29 13" className="prop" />
    </>
  ),

  'seated-figure-four': (
    <>
      <path d="M12 31h15M13 31v12M26 31v12" className="prop" />
      <circle cx="27" cy="9.5" r="4.5" />
      <path d="M16 30l7-15" />
      <path d="M16 30h14l1 13" />
      <path d="M16 29l9-7 6 7" />
    </>
  ),

  'stand-every-30': (
    <>
      <path d="M6 13v17h10M7 30v13M15 30v13" className="prop" />
      <circle cx="32" cy="9" r="4.5" />
      <path d="M32 14.5v14M32 28.5l-4 14M32 28.5l4 14M32 18l-5 9M32 18l5 9" />
      <path d={`M22 26V14${head(22, 14, 'up')}`} className="cue" />
    </>
  ),

  'shoulder-rolls': (
    <>
      <circle cx="24" cy="11" r="4.5" />
      <path d="M24 16.5V22M12 30q0-8 12-8t12 8" />
      <path d={`M7 20a6 6 0 0 1 10-4${head(17, 16, 'right')}`} className="cue" />
      <path d={`M41 20a6 6 0 0 0-10-4${head(31, 16, 'left')}`} className="cue" />
    </>
  ),

  'neck-range': (
    <>
      <circle cx="24" cy="19" r="6" />
      <path d="M24 25v6M12 40q0-9 12-9t12 9" />
      <path d={`M13 13a13 13 0 0 0 0 12${head(13, 25, 'down')}`} className="cue" />
      <path d={`M35 13a13 13 0 0 1 0 12${head(35, 25, 'down')}`} className="cue" />
    </>
  ),

  // ── left–right balance ────────────────────────────────────────────────────
  'single-leg-glute-bridge': (
    <>
      {floor}
      <circle cx="7" cy="38" r="4.5" />
      <path d="M12 40l15-12 8-3 2 17M27 28l16-9" />
      <path d={`M21 22v-7${head(21, 15, 'up')}`} className="cue" />
    </>
  ),

  'split-squat': (
    <>
      {floor}
      <circle cx="24" cy="8" r="4.5" />
      <path d="M24 13.5V28M24 28l8 6v8.5M24 28l-8 11-7 2" />
      <path d="M24 17l-4 8M24 17l4 8" />
      <path d={`M40 24v8${head(40, 32, 'down')}`} className="cue" />
    </>
  ),

  'one-arm-row': (
    <>
      <path d="M3 26h12M5 26v17" className="prop" />
      <circle cx="11" cy="17" r="4.5" />
      <path d="M15.5 20L32 26M32 26l-1 17M32 26l5 17" />
      <path d="M17 21.5l-3 4.5" />
      <path d="M20 22.5l1 10" />
      <rect x="18" y="33" width="6" height="5" rx="1" className="prop" />
      <path d={`M27 36v-8${head(27, 28, 'up')}`} className="cue" />
    </>
  ),

  'suitcase-carry': (
    <>
      {floor}
      <circle cx="22" cy="8" r="4.5" />
      <path d="M22 13.5V28M22 28l-5 14.5M22 28l5 14.5" />
      <path d="M22 17l-6 9M22 17l7 10" />
      <rect x="26" y="27" width="8" height="7" rx="1.5" className="prop" />
    </>
  ),

  'pallof-press': (
    <>
      {floor}
      <path d="M5 8v35" className="prop" />
      <path d="M5 19L38 18" className="prop" />
      <circle cx="28" cy="8" r="4.5" />
      <path d="M28 13.5V29M28 29l-4 13.5M28 29l4 13.5" />
      <path d="M28 18h11" />
      <path d={`M42 24h-6${head(36, 24, 'left')}`} className="cue" />
    </>
  ),

  'seated-side-bend': (
    <>
      <path d="M13 32h22M14 32v11M34 32v11" className="prop" />
      <circle cx="17" cy="12" r="4.5" />
      <path d="M24 31Q24 22 20 17" />
      <path d="M22 18Q27 2 9 3" />
      <path d="M22 21l-6 10" />
      <path d="M21 31.5l-1 11.5M27 31.5l1 11.5" />
    </>
  ),

  'single-leg-stand': (
    <>
      {floor}
      <circle cx="24" cy="8" r="4.5" />
      <path d="M24 13.5V28M24 28v14.5M24 28l8 4-1 8" />
      <path d="M24 17l-8 7M24 17l8 7" />
    </>
  ),

  'eye-break-2020': (
    <>
      <path d="M4 24q9-9 18 0q-9 9-18 0z" />
      <circle cx="13" cy="24" r="2.5" />
      <path d="M26 24h12" className="cue" strokeDasharray="2 4" />
      <path d="M38 30l4-8 4 8" className="prop" />
    </>
  ),
};

/** Coarse shapes by `illustration` field, for exercises without their own drawing. */
const BY_KIND: Record<string, string> = {
  'chin-tuck': 'chin-tuck',
  'wall-angel': 'wall-angel',
  'thoracic-ext': 'cat-cow',
  'scap-retract': 'scapular-retraction',
  core: 'dead-bug',
  stretch: 'doorway-pec-stretch',
  'stand-up': 'stand-every-30',
};

export function ExerciseIllustration({
  id,
  fallback,
  large = false,
}: {
  id: string;
  fallback?: string;
  /** Readable from across a room (the in-session break banner). */
  large?: boolean;
}): ReactNode {
  const drawing =
    DRAWINGS[id] ?? DRAWINGS[BY_KIND[fallback ?? ''] ?? ''] ?? DRAWINGS['stand-every-30'];
  return (
    <svg
      viewBox="0 0 48 48"
      className={`exercise-art ${large ? 'size-24' : 'size-12'} shrink-0 rounded-xl bg-surface p-1 text-ink`}
      aria-hidden
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {drawing}
    </svg>
  );
}

/** Ids with a dedicated drawing — the test keeps the library fully covered. */
export const ILLUSTRATED_IDS = Object.keys(DRAWINGS);
